import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hook/useAuth';
import axios from 'axios';
import { baseURL } from '../../../config/api';
import { PleasewaitPopup } from '../../../components/PleasewaitPopup';
import { validateMin } from '../../../utils/validateMin';
import moment from 'moment';
import { CustomDateField } from '../../../components/CustomDateField';
import { getLocation } from '../../../services/Location';

const AddDailyEstimate = () => {
    const navigate = useNavigate();
    const { auth }: any = useAuth();
    const [dailyEstimateData, setDailyEstimateData] = useState<any>({
        estimateId: 0,
        locationId: 0,
        technicianId: 0,
        shiftDate: moment().format("YYYY-MM-DD"),
        shiftStart: "",
        shiftEnd: "",
        plannedBreakMinutes: ""
    });
    const [locationId, setLocationId] = useState<number>(0);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [userOptions, setUserOptions] = useState([]);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);

    useEffect(() => {
        getLocation()
            .then((data) => {
                setLocationOptions(() => data?.map((loc: any) => {
                    return {
                        value: loc?.id,
                        label: loc?.locationName
                    }
                }));

                setLocationId(data[0]?.id || 0);
            })
            .catch((error) => {
                console.error(error)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        axios.get(`${baseURL}users`)
            .then((response) => {
                setUserOptions(response?.data?.map((item: any) => {
                    return {
                        value: item?.id,
                        label: `${item?.firstName} ${item?.lastName}`
                    }
                }))

                setDailyEstimateData({ ...dailyEstimateData, technicianId: response?.data[0]?.id || 0 });
            }).catch(error => {
                setDailyEstimateData({ ...dailyEstimateData, technicianId: 0 })
                console.error(error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        await axios
            .post(
                `${baseURL}dailyEstimates?userId=${auth?.id}`,
                JSON.stringify({
                    ...dailyEstimateData,
                    locationId: locationId
                }),
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                }
            )
            .then(() => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error:", error);
            });
    }

    return (
        <>
            <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
                <div className="w-full bg-white p-4 rounded-md">
                    <h2 className={`text-lg font-semibold`}>Add Daily Estimates</h2>

                    <div className="mt-2 p-2">
                        <form onSubmit={handleSubmit}>
                            <div className="w-full">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                                    {/* First Column */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Technician Name</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={dailyEstimateData?.technicianId}
                                                onChange={(e) => setDailyEstimateData({ ...dailyEstimateData, technicianId: e.target.value || 0 })}
                                                required
                                            >
                                                {
                                                    userOptions?.map((user: any) => (
                                                        <option
                                                            key={user?.value}
                                                            value={user?.value}
                                                        >
                                                            {user?.label}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Location</label>
                                            <select
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={locationId}
                                                onChange={(e) => setLocationId(parseInt(e.target.value) || 0)}
                                                required
                                            >
                                                {
                                                    locationOptions?.map((loc: any, index: number) => (
                                                        <option key={index} value={loc?.value}>
                                                            {loc?.label}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Shift Start</label>
                                            <input
                                                type="time"
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                placeholder="Shift Start"
                                                value={dailyEstimateData?.shiftStart}
                                                onChange={(e) => setDailyEstimateData({ ...dailyEstimateData, shiftStart: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Planned Break Minutes</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0}
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                placeholder="Planned Break Minutes"
                                                value={dailyEstimateData?.plannedBreakMinutes}
                                                onChange={(e) => setDailyEstimateData({ ...dailyEstimateData, plannedBreakMinutes: validateMin(e.target.value, 0) })}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Second Column */}
                                    <div className="space-y-4">
                                        <div className="h-[3.125rem]"></div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Shift Date</label>
                                            <CustomDateField
                                                value={dailyEstimateData?.shiftDate}
                                                onChange={(value) => setDailyEstimateData({ ...dailyEstimateData, shiftDate: value })}
                                                required={true}
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-secondary">Shift End</label>
                                            <input
                                                type="time"
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                placeholder="Shift End"
                                                value={dailyEstimateData?.shiftEnd}
                                                onChange={(e) => setDailyEstimateData({ ...dailyEstimateData, shiftEnd: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-[#FFFFFF] text-primary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}

export default AddDailyEstimate